import { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { useNavigate, useParams } from "react-router-dom";
import { useApi } from "@/hooks/useApi";
import type { EmpresaDatosAnio } from "@/@types/Api_Extracted";
import { useAppForm, useSelector } from "@/hooks/useForm";
import {
	Table,
	TableHeader,
	TableRow,
	TableHead,
	TableBody,
	ErrorRow,
	SkeletonRows,
	NoResultsRow,
} from "@/components/ui/table";
import { DatosAnioInputRowMemo } from "./DatosAnioInputRow";
import { useTour } from "@reactour/tour";
import { toast } from "react-toastify";
import { UseQueryResult } from "../Input/InputTable";

export function DatosAnioInputTable(props: {
	readonly datosAnio: UseQueryResult<EmpresaDatosAnio>;
}) {
	const { isOpen, setIsOpen } = useTour();
	const params = useParams();

	const api = useApi();
	const navigate = useNavigate();

	const queryDatosAnio = props.datosAnio;
	const mutateDatosAnio = api.useMutation(
		"post",
		"/v1/Empresa/DatosAnio/{anio}",
	);
	const deleteDatosAnio = api.useMutation(
		"delete",
		"/v1/Empresa/DatosAnio/{anio}",
	);

	const isLoading = queryDatosAnio.isPending;
	const isError = queryDatosAnio.isError;

	const defaultValues = useMemo(() => {
		return queryDatosAnio.data;
	}, [queryDatosAnio.data]);

	const myForm = useAppForm({
		defaultValues: defaultValues,
		onSubmit: ({ value }) => {
			if (
				params.anio !== "new" &&
				(!params.anio || !Number.isInteger(Number(params.anio)))
			) {
				toast("Año no reconocido.", { type: "error" });
				return;
			}


			if (!value.anio && params.anio !== "new")
				value.anio = Number(params.anio);
			mutateDatosAnio.mutate(
				{
					body: value,
					params: {
						path: { anio: params.anio },
					},
				}, {
				onError: (error) => {
					console.error("Error al guardar los datos:", error);
					if (
						error.code ===
						"ALREADY_CREATED"
					) {
						toast("Ya hay datos sobre ese año.", { type: "warning" });
					} else {
						toast("Error al guardar los datos.", { type: "error" });
					}
				},
				onSuccess: () => {
					toast("Guardado.", { type: "success" });
					void queryDatosAnio.refetch();
					// TODO - Sustituir por invalidateCache
					void navigate("/user/empresa");
				},
			});
		},
	});

	const formData = useSelector(myForm.store, (state) => state.values);
	const fields = myForm.state.isPristine ? defaultValues : formData;
	const isSuccess = !isLoading && !isError;
	const isEmpty = isSuccess && !fields;

	const onEliminar = async () => {
		try {
			await deleteDatosAnio.mutateAsync({
				params: {
					path: {
						anio: params.anio ?? "",
					},
				},
			});

			toast("Datos eliminados.", { type: "success" });

			void navigate("/user/empresa");
		} catch (error) {
			console.error("Error al eliminar los datos:", error);
			toast("Error al eliminar los datos.", { type: "error" });
		}
	};

	return (
		<form
			onSubmit={(e) => {
				e.preventDefault();
				// Wait for error handlers, blur events, etc. to finish
				if (isOpen) {
					setIsOpen(false); // Avanza al siguiente paso del tour
				} else {
					setTimeout(() => void myForm.handleSubmit(), 10);
				}
			}}
		>
			<Table className="w-full">
				<TableHeader>
					<TableRow>
						{params.anio !== "new" || <TableHead>Año</TableHead>}
						<TableHead data-tour-id="header-trabajadores">
							Trabajadores
						</TableHead>
						<TableHead data-tour-id="header-materia-prima">
							Materia Prima
						</TableHead>
						<TableHead data-tour-id="header-desechos">Desechos</TableHead>
						<TableHead data-tour-id="header-facturacion">Facturación</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody className="text-center">
					{isError && (
						<ErrorRow message="Error al cargar la configuración." colSpan={4} />
					)}
					{isLoading && <SkeletonRows rows={10} colSpan={4} />}
					{isEmpty && (
						<NoResultsRow
							message="No se ha creado la configuración"
							colSpan={4}
						/>
					)}

					{isSuccess && fields && (
						<DatosAnioInputRowMemo
							isNew={params.anio === "new"}
							form={myForm}
						/>
					)}
				</TableBody>
			</Table>
			<div className="float-left mt-4 flex gap-2">
				<Button
					variant="primary"
					type="button"
					onClick={() => void onEliminar()}
				>
					Eliminar
				</Button>
			</div>

			<div className="float-right mt-4 flex gap-2">
				<Button
					variant="outline"
					type="button"
					onClick={() => void navigate("/user/empresa")}
				>
					Cancelar
				</Button>
				<Button variant="primary" type="submit" data-tour-id="submit-button">
					Guardar
				</Button>
			</div>
		</form>
	);
}
